/**
 * Componente atómico para el badge del botón
 *
 * Maneja el renderizado del badge, incluyendo la lógica
 * de cuándo mostrarlo y cómo posicionarlo.
 */

import React from 'react';
import { Badge } from '@/modules/Badge';
import { BADGE_POSITION_STYLE, BADGE_POSITION_CLASSES } from '../styles/buttonVariants';
import { BadgeColor, BadgeTheme, BadgeSize } from '@/shared/types/badge';
import { IconFontColor, IconType, AnyIconDefinition } from '@/shared/types/types';

export interface ButtonBadgeProps {
  /** Si el badge debe mostrarse */
  shouldShow: boolean;
  /** Color del badge */
  badgeColor?: BadgeColor;
  /** Label del badge */
  badgeLabel?: string;
  /** Tema del badge */
  badgeTheme?: BadgeTheme;
  /** Tamaño del badge */
  badgeSize?: BadgeSize;
  /** Si el badge debe tener borde */
  badgeStroke?: boolean;
  /** Icono del badge */
  badgeIcon?: IconType | AnyIconDefinition;
  /** Props de duotone para el icono del badge */
  badgeIconDuotonePrimary?: IconFontColor;
  badgeIconDuotoneSecondary?: IconFontColor;
  badgeIconDuotoneOpacityPrimary?: number;
  badgeIconDuotoneOpacitySecondary?: number;
  /** Si el badge está en estado de carga */
  badgeLoading?: boolean;
  /** Si el badge debe posicionarse absolutamente (para icon buttons) */
  isAbsolute?: boolean;
}

/**
 * Renderiza el badge del botón
 *
 * @example
 * ```tsx
 * <ButtonBadge
 *   shouldShow={true}
 *   badgeColor="accent"
 *   badgeLabel="5"
 *   badgeSize="sm"
 *   isAbsolute={false}
 * />
 * ```
 */
export const ButtonBadge: React.FC<ButtonBadgeProps> = ({
  shouldShow,
  badgeColor,
  badgeLabel,
  badgeTheme = 'solid',
  badgeSize = 'sm',
  badgeStroke,
  badgeIcon,
  badgeIconDuotonePrimary,
  badgeIconDuotoneSecondary,
  badgeIconDuotoneOpacityPrimary,
  badgeIconDuotoneOpacitySecondary,
  badgeLoading,
  isAbsolute = false,
}) => {
  if (!shouldShow) {
    return null;
  }

  const badge = (
    <Badge
      color={badgeColor || 'accent'}
      label={badgeLoading ? undefined : badgeLabel}
      theme={badgeTheme}
      size={badgeSize}
      stroke={badgeStroke}
      icon={badgeLoading ? undefined : badgeIcon}
      iconDuotonePrimary={badgeIconDuotonePrimary}
      iconDuotoneSecondary={badgeIconDuotoneSecondary}
      iconDuotoneOpacityPrimary={badgeIconDuotoneOpacityPrimary}
      iconDuotoneOpacitySecondary={badgeIconDuotoneOpacitySecondary}
      loading={badgeLoading}
      state={badgeLoading ? { loading: true } : undefined}
    />
  );

  // Si debe posicionarse absolutamente (para icon buttons sin children)
  if (isAbsolute) {
    return (
      <div className={BADGE_POSITION_CLASSES} style={BADGE_POSITION_STYLE}>
        {badge}
      </div>
    );
  }

  // Para botones con texto, el badge se renderiza inline
  // El badge mantiene su padding interno original del componente Badge
  // Agregamos margin izquierdo para separarlo del texto del botón
  // El badge ya tiene ui-inline-flex, así que solo necesitamos el margin
  return <span className="ui-ml-4 ui-shrink-0">{badge}</span>;
};

ButtonBadge.displayName = 'ButtonBadge';
